<script setup>
import { ref, computed, onMounted } from 'vue'
import { useGlobalStore } from '@stores/global'
import { useGalleryStore } from '@stores/gallery'
import Loading from '@components/global/Loading.vue'
import Hero from '@sections/global/Hero.vue'
import ContentFrame from '@components/global/ContentFrame.vue'
import ContentSection from '@components/global/ContentSection.vue'
import Input from '@components/forms/Input.vue'
import Textarea from '@components/forms/Textarea.vue'
import Button from '@components/forms/Button.vue'
import Datetime from '@components/forms/Datetime.vue'
import Select from '@components/forms/Select.vue'
import Toggle from '@components/forms/Toggle.vue'
import EventCard from '@components/index/EventCard.vue'
import TransitionGrid from '@components/global/transitions/TransitionGrid.vue'
import TransitionFade from '@components/global/transitions/TransitionFade.vue'
import GalleryCard from '@components/global/GalleryCard.vue'

const global = useGlobalStore()
const galleries = useGalleryStore()

const page = computed(() => global.pages.find((item) => item.slug === 'gallery'))
const hero = computed(() => (page.value?.content ?? []).find((block) => block.type === 'hero_global'))
const date = ref(new Date())
const filters = ref({})
const activeTab = ref('photos')
const tabs = ref([
	{
		name: 'Photos',
		slug: 'photos',
	},
	{
		name: 'Videos',
		slug: 'videos',
	},
])

onMounted(() => {
	galleries.getGalleries()
})

const filteredGalleries = computed(() => {
	return galleries.galleries.filter((gallery) => {
		const dateMatch = !filters.value.dateRange || filters.value.dateRange.length === 0 || (gallery.start_at >= filters.value.dateRange[0] && gallery.start_at <= filters.value.dateRange[1])
		const gameMatch = !filters.value.game || filters.value.game.length === 0 || gallery.games.some((game) => filters.value.game.includes(game.slug))
		const eventMatch = !filters.value.event || filters.value.event.length === 0 || filters.value.event.includes(gallery.slug)
		return dateMatch && gameMatch && eventMatch
	})
})

const eventOptions = computed(() => {
	return galleries.galleries.map((gallery) => ({
		name: gallery.title,
		slug: gallery.slug,
	}))
})

const clear = () => {
	filters.value = {}
}
</script>

<template>
	<Loading v-if="galleries.galleries?.length === 0" />
	<template v-else>
		<Hero :title="hero?.data?.title ?? 'Media Galleries'" :description="hero?.data?.description ?? 'Explore moments from SNK esports tournaments and events.'" />
		<div class="mb-5">
			<div class="container">
				<Toggle :options="tabs" v-model="activeTab" />
			</div>
		</div>
		<section v-if="activeTab == 'photos'">
			<div class="container">
				<div class="relative flex items-center gap-3">
					<div class="flex grow flex-col gap-3 md:flex-row">
						<Datetime class="secondary" v-model="filters.dateRange" placeholder="Dates" :time-config="{ enableTimePicker: false }" :auto-apply="true" timezone="UTC" :range="true" />
						<Select class="secondary" :options="galleries.games" v-model="filters.game" placeholder="Game" />
						<Select class="secondary" :options="eventOptions" v-model="filters.event" placeholder="Event" />
					</div>
					<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					<div class="flex gap-3">
						<Button class="secondary" @click="clear">Clear</Button>
					</div>
				</div>
			</div>
		</section>
		<section class="my-10" v-if="activeTab == 'photos'">
			<div class="container flex flex-col gap-5">
				<GalleryCard v-for="gallery in filteredGalleries" :key="gallery.slug" :gallery="gallery" />
			</div>
		</section>
		<section class="my-10" v-if="activeTab == 'videos'">
			<div class="container">
				<div class="grid grid-cols-1 gap-5 xl:grid-cols-2">
					<div class="w-full overflow-hidden rounded-lg bg-black shadow-xl" v-for="video in global.videos" :key="video.ulid">
						<div class="aspect-video w-full">
							<iframe width="100%" height="100%" :src="'https://www.youtube.com/embed/' + video.youtube_id + '?modestbranding=1&enablejsapi=1&widgetid=3&showinfo=0&iv_load_policy=3&fs=1'" frameborder="0" allowfullscreen v-if="video.youtube_id"></iframe>
						</div>
						<div class="p-5">
							<h6 class="w-5/6 leading-none text-white">{{ video.title }}</h6>
						</div>
					</div>
				</div>
			</div>
		</section>
	</template>
</template>

<style scoped>
@reference '../../css/theme.css';

.list-leave-active {
	@apply absolute;
}
.grid-leave-active {
	@apply absolute;
}
</style>
